import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Save, Eye, Building2, User, ShieldAlert, StickyNote, UserSearch,
} from 'lucide-react';
import { LineasEditor } from '@/components/LineasEditor';
import { ClienteSelector } from '@/components/ClienteSelector';
import { usePresupuestos, useClientes } from '@/hooks/useData';
import { calcularTotales, formatEUR } from '@/lib/calc';
import type { Presupuesto, EstadoPresupuesto, Cliente } from '@/lib/types';

export default function EditorPresupuesto() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lista, guardar } = usePresupuestos();
  const { clientes } = useClientes();
  const [draft, setDraft] = useState<Presupuesto | null>(null);
  const [saved, setSaved] = useState(false);
  const [clienteSelectorOpen, setClienteSelectorOpen] = useState(false);

  useEffect(() => {
    const found = lista.find(p => p.id === id);
    if (found) setDraft({ ...found });
  }, [id, lista]);

  const set = useCallback(<K extends keyof Presupuesto>(field: K, value: Presupuesto[K]) => {
    setDraft(d => d ? { ...d, [field]: value } : d);
    setSaved(false);
  }, []);

  const fillCliente = (c: Cliente) => {
    setDraft(d => d ? {
      ...d,
      clienteId: c.id,
      clienteNombre: c.nombre,
      clienteNIF: c.nif,
      clienteDireccion: [c.direccion, c.cp, c.ciudad].filter(Boolean).join(', '),
      clienteEmail: c.email,
      clienteTelefono: c.telefono,
    } : d);
    setSaved(false);
  };

  const handleSave = () => {
    if (draft) { guardar(draft); setSaved(true); }
  };

  if (!draft) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Presupuesto no encontrado.
      </div>
    );
  }

  const totales = calcularTotales(draft.lineas);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-30"
        style={{ boxShadow: '0 1px 8px -2px color-mix(in srgb, var(--foreground) 5%, transparent)' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="h-9 w-9">
              <ArrowLeft size={18} />
            </Button>
            <div>
              <p className="font-bold text-sm font-mono text-primary">{draft.numero}</p>
              <p className="text-xs text-muted-foreground">{draft.clienteNombre || 'Sin cliente'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/preview/${draft.id}`)} className="gap-1.5">
              <Eye size={15} /> Vista previa
            </Button>
            <Button size="sm" onClick={handleSave} className="gap-1.5">
              <Save size={15} /> {saved ? '¡Guardado!' : 'Guardar'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6 pb-16">

        {/* ── Bloque 1: Datos del presupuesto ── */}
        <Section icon={<ShieldAlert size={16} />} title="Datos del presupuesto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Número">
              <Input value={draft.numero} onChange={e => set('numero', e.target.value)} className="font-mono" />
            </Field>
            <Field label="Fecha">
              <Input type="date" value={draft.fecha} onChange={e => set('fecha', e.target.value)} />
            </Field>
            <Field label="Válido hasta">
              <Input type="date" value={draft.fechaValidez} onChange={e => set('fechaValidez', e.target.value)} />
            </Field>
            <Field label="Estado">
              <Select value={draft.estado} onValueChange={v => set('estado', v as EstadoPresupuesto)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Borrador">Borrador</SelectItem>
                  <SelectItem value="Enviado">Enviado</SelectItem>
                  <SelectItem value="Aceptado">Aceptado</SelectItem>
                  <SelectItem value="Rechazado">Rechazado</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </Section>

        {/* ── Bloque 2: Datos del cliente ── */}
        <Section
          icon={<User size={16} />}
          title="Datos del destinatario"
          action={
            <Button variant="outline" size="sm" onClick={() => setClienteSelectorOpen(true)} className="gap-1.5 text-xs">
              <UserSearch size={14} /> Seleccionar cliente
            </Button>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nombre / Razón social">
              <Input value={draft.clienteNombre} onChange={e => set('clienteNombre', e.target.value)} placeholder="Juan García López" />
            </Field>
            <Field label="NIF / CIF">
              <Input value={draft.clienteNIF} onChange={e => set('clienteNIF', e.target.value)} placeholder="12345678A" className="font-mono" />
            </Field>
            <Field label="Dirección" className="sm:col-span-2">
              <Input value={draft.clienteDireccion} onChange={e => set('clienteDireccion', e.target.value)} placeholder="Calle, número, piso, localidad" />
            </Field>
            <Field label="Email">
              <Input type="email" value={draft.clienteEmail} onChange={e => set('clienteEmail', e.target.value)} placeholder="correo@ejemplo.com" />
            </Field>
            <Field label="Teléfono">
              <Input value={draft.clienteTelefono} onChange={e => set('clienteTelefono', e.target.value)} placeholder="600 000 000" />
            </Field>
          </div>
        </Section>

        {/* ── Bloque 3: Datos siniestro ── */}
        <Section icon={<Building2 size={16} />} title="Datos de siniestro / seguro (opcional)">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Aseguradora">
              <Input value={draft.aseguradora} onChange={e => set('aseguradora', e.target.value)} placeholder="AXA, Mapfre, Allianz…" />
            </Field>
            <Field label="Nº Siniestro">
              <Input value={draft.numeroSiniestro} onChange={e => set('numeroSiniestro', e.target.value)} placeholder="SIN-AAAA-NNNN" className="font-mono" />
            </Field>
            <Field label="Matrícula">
              <Input value={draft.matricula} onChange={e => set('matricula', e.target.value)} placeholder="0000 AAA" className="font-mono uppercase" />
            </Field>
          </div>
        </Section>

        {/* ── Bloque 4: Líneas ── */}
        <Section icon={<StickyNote size={16} />} title="Partidas del presupuesto">
          <LineasEditor lineas={draft.lineas} onChange={lineas => set('lineas', lineas)} />

          {draft.lineas.length > 0 && (
            <div className="flex justify-end mt-4">
              <div className="w-72 space-y-1.5 border border-border rounded-xl p-4 bg-card text-sm">
                <Row label="Subtotal" value={formatEUR(totales.subtotal)} />
                {totales.totalDescuento > 0 && (
                  <Row label="Descuentos" value={`− ${formatEUR(totales.totalDescuento)}`} className="text-emerald-600" />
                )}
                <Separator className="my-1" />
                <Row label="Base imponible" value={formatEUR(totales.baseImponible)} bold />
                {totales.gruposIVA.filter(g => g.cuota > 0).map(g => (
                  <Row key={g.tipo} label={`IVA ${g.tipo}%`} value={formatEUR(g.cuota)} />
                ))}
                <Separator className="my-1" />
                <div className="flex justify-between items-center bg-primary text-primary-foreground rounded-lg px-3 py-2 mt-1">
                  <span className="font-bold tracking-wide">TOTAL</span>
                  <span className="font-mono font-black text-lg">{formatEUR(totales.totalGeneral)}</span>
                </div>
              </div>
            </div>
          )}
        </Section>

        {/* ── Bloque 5: Notas ── */}
        <Section icon={<StickyNote size={16} />} title="Notas y condiciones">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Observaciones">
              <Textarea value={draft.notas} onChange={e => set('notas', e.target.value)} rows={4} placeholder="Notas adicionales para el cliente…" />
            </Field>
            <Field label="Condiciones">
              <Textarea value={draft.condiciones} onChange={e => set('condiciones', e.target.value)} rows={4} placeholder="Condiciones del presupuesto…" />
            </Field>
          </div>
        </Section>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => navigate('/')}>Cancelar</Button>
          <Button variant="outline" onClick={() => navigate(`/preview/${draft.id}`)} className="gap-1.5">
            <Eye size={15} /> Vista previa / PDF
          </Button>
          <Button onClick={handleSave} className="gap-1.5">
            <Save size={15} /> Guardar presupuesto
          </Button>
        </div>
      </main>

      <ClienteSelector
        open={clienteSelectorOpen}
        onClose={() => setClienteSelectorOpen(false)}
        clientes={clientes}
        onSelect={fillCliente}
      />
    </div>
  );
}

/* ── Helpers UI ── */
function Section({ icon, title, action, children }: {
  icon: React.ReactNode; title: string;
  action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <span className="text-primary">{icon}</span>
          <span className="font-semibold text-sm">{title}</span>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

function Row({ label, value, bold = false, className = '' }: { label: string; value: string; bold?: boolean; className?: string }) {
  return (
    <div className={`flex justify-between ${className}`}>
      <span className={`text-muted-foreground ${bold ? 'font-semibold text-foreground' : ''}`}>{label}</span>
      <span className={`font-mono ${bold ? 'font-bold text-foreground' : ''}`}>{value}</span>
    </div>
  );
}
